import { VitalSignTypes } from 'lib/constants';

const ConfidenceLevel = {
  UNKNOWN: 0,
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
} as const;

const RiskLevel = {
  UNKNOWN: 0,
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
} as const;

export interface BinahVitalSignPayload {
  type?: number;
  value: unknown;
  confidence?: { level: number };
  category?: string;
}

export type BinahHealthReportData = Record<
  string,
  BinahVitalSignPayload | string
>;

export interface RppgFinalPredictions {
  hba1c?: {
    value: number;
    band: string;
    diabetes_risk?: {
      level?: number;
    };
  } | null;
  blood_pressure?: {
    systolic: number;
    diastolic: number;
    hypertension_risk?: { at_risk: boolean; level?: number };
  } | null;
  hemoglobin?: {
    value: number;
    anemia_screening?: { at_risk: boolean };
  } | null;
  heart_rate?: { bpm: number; confidence?: number | string } | null;
  hrv?: {
    rmssd_ms?: number;
    sdnn_ms?: number;
    mean_rri_ms?: number;
    sd1_ms?: number;
    sd2_ms?: number;
    lf_hf_ratio?: number;
    confidence?: {
      rmssd_ms?: string;
      sdnn_ms?: string;
      sd1_ms?: string;
      sd2_ms?: string;
      mean_rri_ms?: string;
    };
  } | null;
  respiratory_rate?: { rpm: number; confidence?: number | string } | null;
  spo2?: { percent: number } | null;
  stress_indices?: {
    prq?: number;
    sns_index?: number;
    pns_index?: number;
    stress_index?: number | null;
    prq_confidence?: string;
  } | null;
  derived_parameters?: {
    mean_arterial_pressure?: number;
    pulse_pressure?: number;
    cardiac_workload?: number;
    sns_zone?: string | number;
    pns_zone?: string | number;
    stress_level?: string | number | null;
  } | null;
  wellness?: {
    wellness_index?: number;
    wellness_level?: number;
    wellness_label?: string;
  } | null;
  [key: string]: unknown;
}

export interface RppgQuality {
  snr_db?: number;
  snr_category?: string;
  signal_snr_db?: number;
  signal_snr_category?: string;
  rescan?: {
    recommended: boolean;
    reasons?: string[];
    n_signals?: number;
  };
  [key: string]: unknown;
}

export interface RppgFinalResultLike {
  predictions?: RppgFinalPredictions | null;
  quality?: RppgQuality | null;
  [key: string]: unknown;
}

function mapConfidenceLevel(
  confidence: string | number | null | undefined,
): number | undefined {
  if (confidence == null || confidence === '') {
    return undefined;
  }
  if (typeof confidence === 'number') {
    if (confidence >= 0.8) return ConfidenceLevel.HIGH;
    if (confidence >= 0.5) return ConfidenceLevel.MEDIUM;
    if (confidence >= 0) return ConfidenceLevel.LOW;
    return ConfidenceLevel.UNKNOWN;
  }

  const normalized = String(confidence).trim().toLowerCase();
  if (normalized === 'high') return ConfidenceLevel.HIGH;
  if (normalized === 'medium' || normalized === 'med') return ConfidenceLevel.MEDIUM;
  if (normalized === 'low') return ConfidenceLevel.LOW;
  return ConfidenceLevel.UNKNOWN;
}

function vitalSign(
  type: number,
  value: unknown,
  confidence?: string | number | null,
): BinahVitalSignPayload {
  const level = mapConfidenceLevel(confidence);
  if (level == null) {
    return { type, value };
  }
  return { type, value, confidence: { level } };
}

function reportValueWithCategory(
  value: unknown,
  category?: string | null,
): BinahVitalSignPayload {
  if (category != null) {
    return { value, category };
  }
  return { value };
}

function mapExplicitRiskLevel(level: number | null | undefined): number | null {
  if (level === RiskLevel.LOW || level === RiskLevel.MEDIUM || level === RiskLevel.HIGH) {
    return level;
  }
  return null;
}

function mapBooleanRisk(atRisk: boolean | null | undefined): number {
  if (atRisk == null) return RiskLevel.UNKNOWN;
  return atRisk ? RiskLevel.HIGH : RiskLevel.LOW;
}

function mapHba1cRisk(band: string | null | undefined): number {
  if (!band) return RiskLevel.UNKNOWN;
  const normalized = band.trim().toLowerCase();
  if (normalized === 'normal') return RiskLevel.LOW;
  if (
    normalized === 'prediab' ||
    normalized === 'prediabetes' ||
    normalized.startsWith('pre')
  ) {
    return RiskLevel.MEDIUM;
  }
  if (
    normalized === 'mod_diab' ||
    normalized === 'sev_diab' ||
    normalized.includes('diab')
  ) {
    return RiskLevel.HIGH;
  }
  return RiskLevel.UNKNOWN;
}

function resolveSnrDb(quality: RppgQuality | null | undefined): number | null {
  if (!quality) return null;
  if (quality.snr_db != null) return Number(quality.snr_db);
  if (quality.signal_snr_db != null) return Number(quality.signal_snr_db);
  return null;
}

function resolveSnrCategory(
  quality: RppgQuality | null | undefined,
): string | undefined {
  return quality?.snr_category ?? quality?.signal_snr_category;
}

export function mapRppgPredictionsToBinahData(
  predictions: RppgFinalPredictions | null | undefined,
  quality?: RppgQuality | null,
): BinahHealthReportData {
  const data: BinahHealthReportData = {};

  Object.keys(VitalSignTypes).forEach(key => {
    data[key] = 'N/A';
  });
  data.SNR = 'N/A';

  if (!predictions && !quality) {
    return data;
  }

  const p = predictions;

  if (p?.heart_rate?.bpm != null) {
    data.PULSE_RATE = vitalSign(
      VitalSignTypes.PULSE_RATE,
      Math.round(p.heart_rate.bpm),
      p.heart_rate.confidence,
    );
  }

  if (p?.respiratory_rate?.rpm != null) {
    data.RESPIRATION_RATE = vitalSign(
      VitalSignTypes.RESPIRATION_RATE,
      Number(p.respiratory_rate.rpm),
      p.respiratory_rate.confidence,
    );
  }

  if (p?.spo2?.percent != null) {
    data.OXYGEN_SATURATION = vitalSign(
      VitalSignTypes.OXYGEN_SATURATION,
      Number(p.spo2.percent),
    );
  }

  if (p?.hrv) {
    if (p.hrv.sdnn_ms != null) {
      data.SDNN = vitalSign(
        VitalSignTypes.SDNN,
        Number(p.hrv.sdnn_ms),
        p.hrv.confidence?.sdnn_ms,
      );
    }
    if (p.hrv.mean_rri_ms != null) {
      data.MEAN_RRI = vitalSign(
        VitalSignTypes.MEAN_RRI,
        Number(p.hrv.mean_rri_ms),
        p.hrv.confidence?.mean_rri_ms,
      );
    }
    if (p.hrv.rmssd_ms != null) {
      data.RMSSD = vitalSign(
        VitalSignTypes.RMSSD,
        Number(p.hrv.rmssd_ms),
        p.hrv.confidence?.rmssd_ms,
      );
    }
    if (p.hrv.sd1_ms != null) {
      data.SD1 = vitalSign(
        VitalSignTypes.SD1,
        Number(p.hrv.sd1_ms),
        p.hrv.confidence?.sd1_ms,
      );
    }
    if (p.hrv.sd2_ms != null) {
      data.SD2 = vitalSign(
        VitalSignTypes.SD2,
        Number(p.hrv.sd2_ms),
        p.hrv.confidence?.sd2_ms,
      );
    }
    if (p.hrv.lf_hf_ratio != null) {
      data.LFHF = vitalSign(VitalSignTypes.LFHF, Number(p.hrv.lf_hf_ratio));
    }
  }

  if (
    p?.blood_pressure?.systolic != null &&
    p?.blood_pressure?.diastolic != null
  ) {
    data.BLOOD_PRESSURE = vitalSign(VitalSignTypes.BLOOD_PRESSURE, {
      systolic: Math.round(p.blood_pressure.systolic),
      diastolic: Math.round(p.blood_pressure.diastolic),
    });
  }

  if (p?.blood_pressure?.hypertension_risk != null) {
    const bpRisk =
      mapExplicitRiskLevel(p.blood_pressure.hypertension_risk.level) ??
      mapBooleanRisk(p.blood_pressure.hypertension_risk.at_risk);
    data.HIGH_BLOOD_PRESSURE_RISK = vitalSign(
      VitalSignTypes.HIGH_BLOOD_PRESSURE_RISK,
      bpRisk,
    );
  }

  if (p?.hemoglobin?.value != null) {
    data.HEMOGLOBIN = vitalSign(
      VitalSignTypes.HEMOGLOBIN,
      Number(p.hemoglobin.value),
    );
  }

  if (p?.hemoglobin?.anemia_screening != null) {
    data.LOW_HEMOGLOBIN_RISK = vitalSign(
      VitalSignTypes.LOW_HEMOGLOBIN_RISK,
      mapBooleanRisk(p.hemoglobin.anemia_screening.at_risk),
    );
  }

  if (p?.hba1c?.value != null) {
    data.HEMOGLOBIN_A1C = vitalSign(
      VitalSignTypes.HEMOGLOBIN_A1C,
      Number(p.hba1c.value),
    );
  }

  if (p?.hba1c?.diabetes_risk?.level != null || p?.hba1c?.band != null) {
    const hba1cRisk =
      mapExplicitRiskLevel(p.hba1c?.diabetes_risk?.level) ??
      mapHba1cRisk(p.hba1c?.band);
    data.HIGH_HEMOGLOBIN_A1C_RISK = vitalSign(
      VitalSignTypes.HIGH_HEMOGLOBIN_A1C_RISK,
      hba1cRisk,
    );
  }

  if (p?.stress_indices) {
    if (p.stress_indices.stress_index != null) {
      data.STRESS_INDEX = vitalSign(
        VitalSignTypes.STRESS_INDEX,
        Number(p.stress_indices.stress_index),
      );
    }
    if (p.stress_indices.prq != null) {
      data.PRQ = vitalSign(
        VitalSignTypes.PRQ,
        Number(p.stress_indices.prq),
        p.stress_indices.prq_confidence,
      );
    }
    if (p.stress_indices.pns_index != null) {
      data.PNS_INDEX = vitalSign(
        VitalSignTypes.PNS_INDEX,
        Number(p.stress_indices.pns_index),
      );
    }
    if (p.stress_indices.sns_index != null) {
      data.SNS_INDEX = vitalSign(
        VitalSignTypes.SNS_INDEX,
        Number(p.stress_indices.sns_index),
      );
    }
  }

  if (p?.derived_parameters) {
    const d = p.derived_parameters;
    if (d.stress_level != null && d.stress_level !== '') {
      data.STRESS_LEVEL = vitalSign(
        VitalSignTypes.STRESS_LEVEL,
        Number(d.stress_level),
      );
    }
    if (d.pns_zone != null && d.pns_zone !== '') {
      data.PNS_ZONE = vitalSign(VitalSignTypes.PNS_ZONE, Number(d.pns_zone));
    }
    if (d.sns_zone != null && d.sns_zone !== '') {
      data.SNS_ZONE = vitalSign(VitalSignTypes.SNS_ZONE, Number(d.sns_zone));
    }
    if (d.mean_arterial_pressure != null) {
      data.MEAN_ARTERIAL_PRESSURE = vitalSign(
        VitalSignTypes.MEAN_ARTERIAL_PRESSURE,
        Number(d.mean_arterial_pressure),
      );
    }
    if (d.pulse_pressure != null) {
      data.PULSE_PRESSURE = vitalSign(
        VitalSignTypes.PULSE_PRESSURE,
        Number(d.pulse_pressure),
      );
    }
    if (d.cardiac_workload != null) {
      data.CARDIAC_WORKLOAD = vitalSign(
        VitalSignTypes.CARDIAC_WORKLOAD,
        Number(d.cardiac_workload),
      );
    }
  }

  if (p?.wellness) {
    if (p.wellness.wellness_index != null) {
      data.WELLNESS_INDEX = vitalSign(
        VitalSignTypes.WELLNESS_INDEX,
        Number(p.wellness.wellness_index),
        p.wellness.wellness_label,
      );
    }
    if (p.wellness.wellness_level != null) {
      data.WELLNESS_LEVEL = vitalSign(
        VitalSignTypes.WELLNESS_LEVEL,
        Number(p.wellness.wellness_level),
        p.wellness.wellness_label,
      );
    }
  } else {
    data.WELLNESS_INDEX = vitalSign(VitalSignTypes.WELLNESS_INDEX, 0);
    data.WELLNESS_LEVEL = vitalSign(VitalSignTypes.WELLNESS_LEVEL, 0);
  }

  const snrDb = resolveSnrDb(quality);
  if (snrDb != null && !Number.isNaN(snrDb)) {
    data.SNR = reportValueWithCategory(snrDb, resolveSnrCategory(quality));
  } else {
    data.SNR = reportValueWithCategory(0, resolveSnrCategory(quality));
  }

  return data;
}

export function mapRppgFinalResultToBinahData(
  result: RppgFinalResultLike | null | undefined | Record<string, unknown>,
): BinahHealthReportData {
  const typed = result as RppgFinalResultLike | null | undefined;
  return mapRppgPredictionsToBinahData(
    typed?.predictions,
    typed?.quality ?? null,
  );
}
